import { getDay, Interval } from "date-fns";
import { useMemo } from "react";

import { eachUtcDayWithinRange, isDateBetweenDays } from "@/utils/dates";

import { TimeReport, TimeReportProject } from "../types";
import { isSameTimeReportProjectItem } from "./isSameTimeReportProjectItem";
import { isTimeReportEditable } from "./isTimeReportEditable";
import { loggedTimeToAggregatedLoggedTime } from "./loggedTimeToAggregatedLoggedTime";
import { reportedTimeToLoggedTime } from "./reportedTimeToLoggedTime";
import { sortTimeReportPaycodes } from "./sortTimeReportPaycodes";
import { sumLoggedTimes } from "./sumLoggedTimes";
import { TimeReportAggregatedLoggedTime } from "./TimeReportAggregatedLoggedTime";
import { TimeReportDayAggregate } from "./TimeReportDayAggregate";
import { TimeReportPayCodeAggregate } from "./TimeReportPayCodeAggregate";
import { TimeReportProjectAggregate } from "./TimeReportProjectAggregate";
import { TimeReportProjectItemAggregate } from "./TimeReportProjectItemAggregate";

export interface TimeReportAggregateOptions {
  from: Date;
  to: Date;
  filterProjects?: (project: TimeReportProject) => boolean;
  filterEmptyItems?: boolean;
  hoursPerDay?: number;
}

export interface TimeReportAggregateReturn {
  days: TimeReportDayAggregate[];
  projects: TimeReportProjectAggregate[];
  aggregateTotalReportedTime: TimeReportAggregatedLoggedTime;
  dateRangeSize: number;
}

type DaysMap = Map<
  string,
  {
    isInTimeReportingPeriod: boolean;
    loggedTimes: TimeReportAggregatedLoggedTime[];
  }
>;

const addItemsToDaysMap = (daysMap: DaysMap, projectItems: TimeReportProjectItemAggregate[]) => {
  projectItems.forEach(item => {
    const { date, loggedTime, isInTimeReportingPeriod } = item;
    const aggregatedLoggedTime = loggedTimeToAggregatedLoggedTime(loggedTime);

    const isoString = date.toISOString();

    const daysMapEntry = daysMap.get(isoString);

    if (typeof daysMapEntry === "undefined") {
      daysMap.set(isoString, {
        loggedTimes: [aggregatedLoggedTime],
        isInTimeReportingPeriod,
      });
    } else {
      daysMapEntry.loggedTimes.push(aggregatedLoggedTime);
      daysMapEntry.isInTimeReportingPeriod = daysMapEntry.isInTimeReportingPeriod || isInTimeReportingPeriod;
    }
  });
};

const aggregateDays = (
  aggregatedRange: Date[],
  daysMap: DaysMap,
  projects: TimeReportProjectAggregate[],
): TimeReportDayAggregate[] => {
  return aggregatedRange.map(date => {
    const mapEntry = daysMap.get(date.toISOString());
    const dayTotalLoggedTime: TimeReportAggregatedLoggedTime = sumLoggedTimes(mapEntry?.loggedTimes ?? []);

    return {
      date,
      dayIndex: getDay(date),
      dayTotalLoggedTime,
      isInTimeReportingPeriod: mapEntry?.isInTimeReportingPeriod ?? false,
      projects: filterProjectsByDay(projects, date),
    };
  });
};

const filterNonEmptyProjects = (projects: TimeReportProjectAggregate[]): TimeReportProjectAggregate[] => {
  return projects
    .filter(project => !project.loggedTime.isEmpty)
    .map(project => ({
      ...project,
      payCodes: project.payCodes
        .filter(payCode => !payCode.loggedTime.isEmpty)
        .map(payCode => ({
          ...payCode,
          items: payCode.items.filter(item => !item.loggedTime.isEmpty),
        })),
    }));
};

const filterNonEmptyOnly = (result: TimeReportAggregateReturn): TimeReportAggregateReturn => {
  return {
    ...result,
    days: result.days
      .filter(d => !d.dayTotalLoggedTime.isEmpty)
      .map(d => ({ ...d, projects: filterNonEmptyProjects(d.projects) })),
    projects: filterNonEmptyProjects(result.projects),
  };
};

const filterProjectsByDay = (projects: TimeReportProjectAggregate[], day: Date): TimeReportProjectAggregate[] => {
  return projects.map(project => {
    const filteredPayCodes = project.payCodes.map(payCode => {
      const filteredItems = payCode.items.filter(item => item.date === day);

      return {
        ...payCode,
        items: filteredItems,
        loggedTime: sumLoggedTimes(filteredItems.map(item => loggedTimeToAggregatedLoggedTime(item.loggedTime))),
      };
    });

    return {
      ...project,
      payCodes: filteredPayCodes,
      loggedTime: sumLoggedTimes(filteredPayCodes.map(payCode => payCode.loggedTime)),
    };
  });
};

const swapItem = (payCode: TimeReportPayCodeAggregate, item: TimeReportProjectItemAggregate): void => {
  const existingItemIndex = payCode.items.findIndex(i => i.date.toISOString() === item.date.toISOString());

  payCode.items.splice(existingItemIndex, 1, item);
  payCode.loggedTime = sumLoggedTimes([payCode.loggedTime, loggedTimeToAggregatedLoggedTime(item.loggedTime)]);
};

const mergePaycodes = (payCodes: TimeReportPayCodeAggregate[]): TimeReportPayCodeAggregate[] => {
  const result: TimeReportPayCodeAggregate[] = [];

  payCodes.forEach(payCode => {
    const existingPaycode = result.find(pc => pc.sourceGuid === payCode.sourceGuid);

    if (existingPaycode) {
      payCode.items.forEach(payCodeItem => {
        if (payCodeItem.isInTimeReportingPeriod) {
          swapItem(existingPaycode, payCodeItem);
        }
      });
    } else {
      result.push(payCode);
    }
  });

  return result;
};

export const useTimeReportAggregate = (
  timeReport: TimeReport,
  {
    from: aggregateStartDate,
    to: aggregateEndDate,
    hoursPerDay = 8,
    filterProjects: projectFilter = () => true,
    filterEmptyItems: nonEmptyOnly,
  }: TimeReportAggregateOptions,
): TimeReportAggregateReturn =>
  useMemo((): TimeReportAggregateReturn => {
    const projects = timeReport.projects.filter(projectFilter);
    const isEditable = isTimeReportEditable(timeReport);

    const aggregateInterval: Interval = {
      start: aggregateStartDate,
      end: aggregateEndDate,
    };

    const aggregateDateRange = eachUtcDayWithinRange(aggregateInterval);

    const payCodes = sortTimeReportPaycodes(timeReport);
    const daysMap: DaysMap = new Map();

    const aggregateProjects: TimeReportProjectAggregate[] = projects.map(project => {
      const projectItemsInDateRange = project.items.filter(i =>
        isDateBetweenDays({
          date: new Date(i.date),
          startDate: aggregateStartDate,
          endDate: aggregateEndDate,
        }),
      );

      const aggregatedPaycodes: TimeReportProjectAggregate["payCodes"] = payCodes.map(payCode => {
        const payCodeItems: TimeReportProjectItemAggregate[] = aggregateDateRange.map(date => {
          const projectItem = projectItemsInDateRange.find(isSameTimeReportProjectItem(payCode.guid, date));

          const isInTimeReportingPeriod = Boolean(projectItem);

          return {
            id: projectItem?.id ?? null,
            date,
            dayIndex: getDay(date),
            loggedTime: reportedTimeToLoggedTime(projectItem?.reportedTime ?? 0, payCode.measureUnit.code),

            isInTimeReportingPeriod,
            isEditable: isInTimeReportingPeriod && isEditable,
            comment: projectItem?.comment ?? null,
            attachments: projectItem?.attachments ?? null,
            itemPath: {
              date,
              payCodeGuid: payCode.guid,
              projectGuid: project.project.guid,
            },
            item: projectItem,
            measureUnit: payCode.measureUnit.code,
          };
        });

        addItemsToDaysMap(daysMap, payCodeItems);

        return {
          sourceGuid: payCode.sourceGuid,
          name: payCode.name,
          measureUnitCode: payCode.measureUnit.code,
          items: payCodeItems,
          loggedTime: sumLoggedTimes(payCodeItems.map(i => loggedTimeToAggregatedLoggedTime(i.loggedTime))),
          payCode,
        };
      });

      const mergedPaycodes = mergePaycodes(aggregatedPaycodes);

      return {
        guid: project.project.guid,
        name: project.projectName,
        payCodes: mergedPaycodes,
        loggedTime: sumLoggedTimes(mergedPaycodes.map(p => p.loggedTime)),
        project,
      };
    });

    const days = aggregateDays(aggregateDateRange, daysMap, aggregateProjects);

    const result = {
      days,
      projects: aggregateProjects,
      aggregateTotalReportedTime: sumLoggedTimes(days.map(p => p.dayTotalLoggedTime)),
      dateRangeSize: days.length,
    };

    return nonEmptyOnly ? filterNonEmptyOnly(result) : result;
  }, [timeReport, aggregateStartDate, aggregateEndDate, hoursPerDay, projectFilter, nonEmptyOnly]);
